import type { ProviderCategoryType } from '@/app/integrations/types';
import {
  type ALL_EVENT_LOG_SEARCH_PARAM_KEYS,
  type EVENT_LOG_SORT_KEYS,
} from '@/router/searchParams/eventLogSearchParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const healthCenter = {
  path: `${instance.path}/health-center`,
  description:
    'A place where a user can see the overall health of their Channel99 setup. The user can see if the C99 tag is placed on their website, TAM has been defined, CRM (Salesforce or HubSpot) has been connected, vendor spend has been updated, users have been invited, audiences have been created, and pixels have been created.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const eventLog = {
  path: `${instance.path}/event-log`,
  description:
    'A list of events that have occurred in the company. These events include administrative actions such as creating, updating, or deleting any object in the system.',
  navigable: true,
  pathParams: {},
  searchParams: {
    'e.typeString': {
      type: 'enum',
      enumValues: ['Note', 'Campaign', 'Admin', 'Spend', 'Activity', 'Pipeline'],
      description: 'A filter for the type of event.',
    },
    'e.sortBy': {
      type: 'enum',
      enumValues: ['sourceString', 'typeString', 'occurredAt'],
      default: 'occurredAt',
      description: 'The field to sort by. Only one field can be sorted at a time.',
    } satisfies RouteSearchParamEnumType<EVENT_LOG_SORT_KEYS>,
    'e.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'desc',
      description:
        'The sort order. Fields with type `number` or `date` are usually best sorted in descending order. While string fields are best sorted in ascending order.',
    },
    'e.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'e.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
    'e.search': {
      type: 'string',
      description: 'A string to search for in the event details or source',
    },
  },
} satisfies NavigableRouteType<ALL_EVENT_LOG_SEARCH_PARAM_KEYS>;

export const integrations = {
  path: `${instance.path}/integrations`,
  description:
    'A page where a user can see all the integrations that are connected to Channel99. The user can see the status of each integration.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const integrationDetail = {
  path: `${instance.path}/integrations/:provider`,
  description:
    'A detail page for a single integration. The user can see the status of the integration, the last time a sync occurred, and the number of connections that have been made. Users can also make new connections or delete existing ones.',
  navigable: true,
  pathParams: {
    provider: {
      type: 'string',
      description: 'The ID of the integration provider the user wishes to view',
      requiresLookup: true,
      lookupType: 'provider',
      required: true,
    },
  },
  searchParams: {},
} satisfies NavigableRouteType;

export const verifyIntegration = {
  path: `${instance.path}/verify-integration`,
  description: 'A redirect page to verify an integration.',
  navigable: false,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const companySettings = {
  path: `${instance.path}/company-settings`,
  description:
    'A page where an admin can edit general information for their C99 organization, specify their Total Addressable Market (TAM) by industries, regions, or company sizes. The C99 website tag can also be found and copied here.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const companySettingsGeneral = {
  ...companySettings,
  path: `${companySettings.path}/general`,
} satisfies NavigableRouteType;

export const companySettingsImpactScore = {
  ...companySettings,
  path: `${companySettings.path}/impact-scores`,
  description: "A page where an admin can configure the Instance's custom impact scoring model.",
} satisfies NavigableRouteType;

export const configureVendors = {
  path: `${instance.path}/configure-vendors`,
  description:
    'A form showing each vendor in the system and the spend for each vendor. The user can manually edit spend by month for each vendor. Users can also see spend numbers synced from integrations here (but not edit them).',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const configureChannels = {
  path: `${instance.path}/configure-channels`,
  description:
    'A form where users can add channel mapping rules to the system. The user can specify which channels are mapped to vendors via medium, source, referral domain, or custom parameters.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const integrationByCategory = (category: ProviderCategoryType = 'all') =>
  `${integrations.path}?category=${category}`;

export const integrationById = (providerId?: string) =>
  providerId && `${integrations.path}/${providerId}`;

export const verifyIntegrationRedirect = (
  instanceSlug: string,
  providerId: string,
  searchParams: URLSearchParams,
) => {
  return `/app/inst/${instanceSlug}/verify-integration/${providerId}?${searchParams?.toString()}`;
};
